import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, PenLine, ExternalLink, X, Loader2, CheckCircle2, Camera, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, scrollToId } from "@/lib/api";
import { GOOGLE_REVIEWS_URL, TRIPADVISOR_URL } from "@/lib/site";

const VISIT_TYPES = ["Families", "Corporate", "Adrenaline Hunters", "Schools"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value, size = 16, onPick }) {
  return (
    <span className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={onPick ? () => onPick(n) : undefined}
          className={onPick ? "transition-transform hover:scale-110" : "cursor-default"}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          data-testid={onPick ? `review-star-${n}` : undefined}
        >
          <Star size={size} className={n <= value ? "fill-[#f8b818] text-[#f8b818]" : "text-white/25"} />
        </button>
      ))}
    </span>
  );
}

function SourceBadge({ source }) {
  if (source === "Google") {
    return (
      <span className="chip !px-2.5 !py-1 !text-[10px]" data-testid="review-source-google">
        <svg width="12" height="12" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z" />
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41.4 35.4 44 30.2 44 24c0-1.3-.1-2.7-.4-3.9z" />
        </svg>
        Google
      </span>
    );
  }
  if (source === "Tripadvisor") {
    return (
      <span className="chip !px-2.5 !py-1 !text-[10px] !text-[#34e0a1]" data-testid="review-source-tripadvisor">
        <span className="flex gap-0.5" aria-hidden="true">
          <span className="h-1.5 w-1.5 rounded-full border border-[#34e0a1]" />
          <span className="h-1.5 w-1.5 rounded-full border border-[#34e0a1]" />
        </span>
        Tripadvisor
      </span>
    );
  }
  return (
    <span className="chip !px-2.5 !py-1 !text-[10px] !text-[#f8b818]" data-testid="review-source-website">
      <img src="/logo.png" alt="Cool Runnings logo" className="h-3.5 w-auto" />
      Website
    </span>
  );
}

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", rating: 5, visit_type: "Families", text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/reviews");
      setReviews(res.data || []);
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handler = () => {
      setSubmitted(false);
      setModalOpen(true);
    };
    window.addEventListener("cr:open-review-modal", handler);
    return () => window.removeEventListener("cr:open-review-modal", handler);
  }, []);

  useEffect(() => {
    if (modalOpen) return;
    const t = setTimeout(() => setSubmitted(false), 400);
    return () => clearTimeout(t);
  }, [modalOpen]);

  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);
  const openedAt = useRef(Date.now());
  useEffect(() => {
    if (modalOpen) openedAt.current = Date.now();
  }, [modalOpen]);

  const perView = () => (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
  const scrollSlides = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };
  const onScrollerScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setPage(Math.round(el.scrollLeft / Math.max(el.clientWidth, 1)));
  };
  const per = perView();
  const firstVisible = Math.min(page * per + 1, reviews.length);
  const lastVisible = Math.min(firstVisible + per - 1, reviews.length);
  const canPrev = page > 0;
  const canNext = lastVisible < reviews.length;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("rating", String(form.rating));
      fd.append("visit_type", form.visit_type);
      fd.append("text", form.text);
      fd.append("website", e.currentTarget?.website?.value || "");
      fd.append("elapsed_ms", String(Date.now() - openedAt.current));
      if (photo) fd.append("photo", photo);
      const res = await api.post("/reviews", fd);
      toast.success(res.data?.message || "Thanks! Your review is awaiting approval.");
      setSubmittedName(form.name.trim());
      setSubmitted(true);
      setForm({ name: "", email: "", rating: 5, visit_type: "Families", text: "" });
      setPhoto(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.[0]?.msg || "Could not submit your review — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="border-y border-[#241c0d]/10 bg-[#fff1d9] py-24" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap justify-end gap-3"
        >
          <button onClick={() => setModalOpen(true)} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="review-write-open-btn">
            <PenLine size={14} /> Write a review
          </button>
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="btn-outline !px-5 !py-2.5 !text-xs" data-testid="review-google-link">
            Review us on Google <ExternalLink size={14} />
          </a>
        </motion.div>

        {reviews.length > 1 && (
          <div className="mt-8 flex items-center justify-between gap-4" data-testid="reviews-slider-nav">
            <span className="text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]" data-testid="reviews-counter">
              Showing {firstVisible}–{lastVisible} of {reviews.length}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => scrollSlides(-1)}
                disabled={!canPrev}
                className="rounded-full border border-white/15 p-2.5 text-[#faf5ea] transition-colors hover:border-[#f8b818] hover:text-[#f8b818] disabled:opacity-30"
                aria-label="Previous reviews"
                data-testid="reviews-prev-btn"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => scrollSlides(1)}
                disabled={!canNext}
                className="rounded-full border border-white/15 p-2.5 text-[#faf5ea] transition-colors hover:border-[#f8b818] hover:text-[#f8b818] disabled:opacity-30"
                aria-label="Next reviews"
                data-testid="reviews-next-btn"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-14 flex justify-center py-10">
            <Loader2 className="animate-spin text-[#f8b818]" size={28} />
          </div>
        ) : reviews.length === 0 ? (
          <div className="card-dark mt-12 p-12 text-center" data-testid="reviews-empty-state">
            <p className="font-display text-3xl text-[#f8b818]">BE THE FIRST</p>
            <p className="mt-2 text-sm text-[#8a7b5e]">No published reviews yet — tell Cape Town how it went.</p>
            <button onClick={() => setModalOpen(true)} className="btn-gold mt-6" data-testid="reviews-empty-cta">
              Write the first review
            </button>
          </div>
        ) : (
          <div
            ref={scrollerRef}
            onScroll={onScrollerScroll}
            className="no-scrollbar mt-6 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2"
            data-testid="reviews-scroller"
          >
            {reviews.map((r, i) => (
              <motion.div
                key={r.id}
                data-review-slide
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.07 }}
                className="card-dark flex w-[85vw] max-w-[460px] shrink-0 snap-start flex-col p-6 sm:w-[calc((100%-20px)/2)] sm:max-w-none lg:w-[calc((100%-40px)/3)]"
                data-testid={`review-card-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Stars value={r.rating} />
                  <SourceBadge source={r.source || "Website"} />
                </div>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-[#8a7b5e]">“{r.text}”</p>
                <div className="mt-5 flex items-center justify-between border-t border-[#241c0d]/10 pt-4">
                  <span className="flex items-center gap-2.5">
                    {r.avatar_url ? (
                      <img src={r.avatar_url} alt={r.name} className="h-8 w-8 rounded-full object-cover" data-testid={`review-avatar-img-${r.id}`} />
                    ) : (
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f8b818]/15 text-[11px] font-extrabold text-[#f8b818]" data-testid={`review-avatar-${r.id}`}>
                        {r.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </span>
                    )}
                    <span className="text-sm font-extrabold">{r.name}</span>
                  </span>
                  <span className="chip !px-3 !py-1 !text-[10px]">{r.visit_type}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setModalOpen(false)}
            data-testid="review-modal"
          >
            <motion.form
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onSubmit={submit}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[28px] bg-[#fff7e8] p-8 pt-10 shadow-[0_0_0_3px_#f8b818,0_0_0_9px_rgba(248,184,24,0.16),0_40px_90px_rgba(20,18,12,0.55)]"
              data-testid="review-form"
            >
              <span className="pointer-events-none absolute inset-x-0 top-0 h-1.5 rounded-t-[28px] bg-gradient-to-r from-[#f8b818] via-[#f9c92e] to-[#f8b818]" />
              <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#f8b818]/25 blur-2xl" />
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                data-testid="review-website-hp"
              />
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="absolute right-4 top-4 rounded-full border border-[#241c0d]/15 bg-white/70 p-2 text-[#6b5a3a] transition-colors hover:border-[#c87700] hover:text-[#c87700]"
                aria-label="Close review form"
                data-testid="review-modal-close-btn"
              >
                <X size={16} />
              </button>
              <h3 className="text-3xl leading-none text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>RATE YOUR RIDE</h3>

              {submitted ? (
                <div className="flex flex-col items-center gap-4 py-8 text-center" data-testid="review-success-view">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f8b818] text-[#14120c] shadow-[0_4px_0_#7a5600,0_10px_20px_rgba(248,184,24,0.45)]">
                    <CheckCircle2 size={32} />
                  </span>
                  <h3 className="text-3xl leading-none text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }} data-testid="review-success-title">
                    REVIEW RECEIVED{submittedName ? `, ${submittedName.split(" ")[0].toUpperCase()}` : ""}!
                  </h3>
                  <p className="max-w-sm text-sm font-bold text-[#3d362c]" data-testid="review-success-copy">
                    It's in our moderation queue and will appear on this website once approved.
                  </p>
                  <p className="max-w-sm text-xs leading-relaxed text-[#5c5240]">
                    Heads-up: Google and Tripadvisor only accept reviews posted through their own apps — nobody can
                    push it there automatically. One tap below posts the same words there:
                  </p>
                  <div className="grid w-full gap-3 sm:grid-cols-2">
                    <a
                      href={GOOGLE_REVIEWS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-gold justify-center !px-4 !py-3 !text-xs"
                      data-testid="review-crosspost-google"
                    >
                      Post it on Google Maps
                    </a>
                    <a
                      href={TRIPADVISOR_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline justify-center !px-4 !py-3 !text-xs"
                      data-testid="review-crosspost-tripadvisor"
                    >
                      Post it on Tripadvisor
                    </a>
                  </div>
                  <button onClick={() => setModalOpen(false)} className="btn-outline w-full justify-center" data-testid="review-success-done-btn">
                    Done
                  </button>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-widest text-[#6b5a3a]">Your rating</label>
                    <div className="mt-2">
                      <Stars value={form.rating} size={26} onPick={(n) => setForm({ ...form, rating: n })} />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      className="input-dark"
                      placeholder="Your name *"
                      required
                      minLength={2}
                      maxLength={60}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="review-form-name-input"
                    />
                    <input
                      className="input-dark"
                      type="email"
                      placeholder="Email (optional)"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="review-form-email-input"
                    />
                  </div>
                  <select
                    className="input-dark"
                    value={form.visit_type}
                    onChange={(e) => setForm({ ...form, visit_type: e.target.value })}
                    data-testid="review-form-visit-type-select"
                  >
                    {VISIT_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[#141416]">
                        {t}
                      </option>
                    ))}
                  </select>
                  <textarea
                    className="input-dark min-h-28"
                    placeholder="How was your day on the track? *"
                    required
                    minLength={10}
                    maxLength={1000}
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                    data-testid="review-form-text-textarea"
                  />
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-widest text-[#6b5a3a]">
                      Your photo (optional — shows next to your review)
                    </label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                      className="input-dark mt-2 !py-2.5 file:mr-3 file:rounded-full file:border-0 file:bg-[#f8b818] file:px-3 file:py-1 file:text-xs file:font-extrabold"
                      data-testid="review-form-photo-input"
                    />
                    {photo && (
                      <p className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold text-green-700" data-testid="review-photo-ready">
                        <Camera size={11} /> {photo.name} — uploaded with your review
                      </p>
                    )}
                  </div>
                  <p className="text-xs leading-relaxed text-[#5c5240]" data-testid="review-form-destination-note">
                    Your review goes to our moderation queue and appears on this website once approved (first name
                    only — POPIA applies). After submitting, you can post the same words on Google Maps or
                    Tripadvisor in one tap.
                  </p>
                  <button type="submit" disabled={submitting} className="btn-gold w-full disabled:opacity-60" data-testid="review-form-submit-btn">
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={15} />}
                    {submitting ? "Sending…" : "Submit review"}
                  </button>
                </div>
              )}
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
